import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { baseLanguage, sameLanguage } from "@lytra/frontend-common";
import { TRANSLATE_CONVERSATION_QUERY } from "./ticketQueries";
import type { MessageContentData } from "./ticketHelpers";

/** The languages the backend translates into (services/translation.py DRAFT_LANGUAGES). */
const THREAD_LANGUAGES = ["de", "en", "fr", "it", "es", "nl", "pt", "pl"];

function storageKey(conversationId: string) {
  return `lytra.threadLanguage.${conversationId}`;
}

function readStored(conversationId: string): string | null {
  try {
    return localStorage.getItem(storageKey(conversationId));
  } catch {
    return null;
  }
}

function store(conversationId: string, language: string | null) {
  try {
    if (language) localStorage.setItem(storageKey(conversationId), language);
    else localStorage.removeItem(storageKey(conversationId));
  } catch {
    // The choice is a convenience; without storage it just isn't remembered.
  }
}

/**
 * Translating a ticket's thread into another language (#913). The choice is
 * remembered per ticket in the browser; choosing the thread's own language shows
 * the original again.
 */
export function useThreadTranslation(
  conversationId: string,
  conversationLanguage: string | null | undefined,
  messageCount: number,
  enabled: boolean,
) {
  const originalLanguage = baseLanguage(conversationLanguage) ?? "de";
  const languageOptions = useMemo(
    () =>
      THREAD_LANGUAGES.includes(originalLanguage)
        ? THREAD_LANGUAGES
        : [originalLanguage, ...THREAD_LANGUAGES],
    [originalLanguage],
  );
  const [requested, setRequested] = useState(() => readStored(conversationId));
  const [shownConversationId, setShownConversationId] =
    useState(conversationId);
  if (shownConversationId !== conversationId) {
    setShownConversationId(conversationId);
    setRequested(readStored(conversationId));
  }

  const wantsTranslation =
    enabled && requested !== null && !sameLanguage(requested, originalLanguage);
  const { data, loading, error, refetch } = useQuery(
    TRANSLATE_CONVERSATION_QUERY,
    {
      variables: {
        conversationId,
        languageCode: requested ?? originalLanguage,
      },
      skip: !wantsTranslation,
    },
  );
  const failed = wantsTranslation && !!error;
  const active = wantsTranslation && !failed && !!data;

  // A message that arrives while the thread is translated is translated too.
  const seenMessageCountRef = useRef(messageCount);
  useEffect(() => {
    const grew = active && seenMessageCountRef.current !== messageCount;
    seenMessageCountRef.current = messageCount;
    // A failure surfaces through `error`.
    if (grew) refetch().catch(() => undefined);
  }, [active, messageCount, refetch]);

  // A failed translation is not retried on the next visit.
  useEffect(() => {
    if (failed) store(conversationId, null);
  }, [failed, conversationId]);

  const translations = useMemo(
    () =>
      new Map(
        active
          ? data.conversationTranslate.map((row) => [
              row.messageId,
              {
                text: row.contentText ?? null,
                html: row.contentHtml ?? null,
                emailFullText: row.emailFullContentText ?? null,
                emailFullHtml: row.emailFullContentHtml ?? null,
                emailSubject: row.emailSubject ?? null,
                emailForwardedSubject: row.emailForwardedSubject ?? null,
                emailForwardedContentText:
                  row.emailForwardedContentText ?? null,
              },
            ])
          : [],
      ),
    [active, data],
  );
  const selectedLanguage =
    wantsTranslation && !failed && requested ? requested : originalLanguage;

  const selectLanguage = useCallback(
    (language: string) => {
      const target = sameLanguage(language, originalLanguage) ? null : language;
      setRequested(target);
      store(conversationId, target);
    },
    [conversationId, originalLanguage],
  );

  const translate = useCallback(
    (messageId: string, content: MessageContentData): MessageContentData => {
      const translation = translations.get(messageId);
      return translation
        ? { ...translation, languageCode: selectedLanguage }
        : content;
    },
    [translations, selectedLanguage],
  );

  return {
    originalLanguage,
    languageOptions,
    selectedLanguage,
    isTranslating: wantsTranslation && loading,
    failed,
    selectLanguage,
    isTranslated: (messageId: string) => translations.has(messageId),
    translate,
  };
}
